import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { FiAward, FiBarChart2, FiLayers, FiSettings, FiTrendingUp, FiSearch as FiSearchIcon, FiDollarSign, FiBookmark, FiGlobe, FiZap } from 'react-icons/fi';
import ucdpLogo from "../assets/logo3.png";
import { getPermissions } from "../lib/permissions";

interface NavbarProps {
  onSearchClick?: () => void;
  mobileMenuOpen?: boolean;
  toggleMobileMenu?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ mobileMenuOpen = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(mobileMenuOpen);
  const [showExploreDropdown, setShowExploreDropdown] = useState(false);
  const [showTrackDropdown, setShowTrackDropdown] = useState(false);
  const [showControlDropdown, setShowControlDropdown] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const [mobileTrackOpen, setMobileTrackOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const exploreRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem("userRole");
  const perms = getPermissions(userRole);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node))
        setShowDropdown(false);
    }
    if (showDropdown) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (exploreRef.current && !exploreRef.current.contains(event.target as Node))
        setShowExploreDropdown(false);
      if (trackRef.current && !trackRef.current.contains(event.target as Node))
        setShowTrackDropdown(false);
    }
    if (showExploreDropdown || showTrackDropdown)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExploreDropdown, showTrackDropdown]);

  const isExploreActive = () =>
    ['/startups', '/startup-discovery', '/vc-intelligence', '/usecase-trends', '/discover'].some(
      path => location.pathname.startsWith(path)
    );

  const isTrackActive = () =>
    ['/engagement-repo', '/acquisition-dashboard'].some(
      path => location.pathname.startsWith(path)
    );

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("userRole");
    localStorage.removeItem("authToken");
    localStorage.removeItem("uid");
    setShowDropdown(false);
    navigate('/login');
  };


  const navLinkBaseStyle = `font-['Lora'] text-[16px] font-[400] leading-[100%] tracking-[0] transition-colors`;
  const isActiveRoute = (path: string) => location.pathname === path;

  const SectionLabel = ({ label }: { label: string }) => (
    <div className="px-4 pt-3 pb-1">
      <span className="text-[10px] font-semibold tracking-widest uppercase text-white/30">{label}</span>
    </div>
  );

  const DropdownLink = ({ to, icon, label, sub, onClick }: { to: string; icon: React.ReactNode; label: string; sub: string; onClick: () => void }) => (
    <Link to={to} onClick={onClick} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-white">
      <span className="flex-shrink-0">{icon}</span>
      <div>
        <span className="text-sm font-medium">{label}</span>
        <p className="text-xs text-white/45">{sub}</p>
      </div>
    </Link>
  );

  const closeAll = () => { setShowExploreDropdown(false); setShowTrackDropdown(false); };

  return (
    <nav className={`fixed top-0 left-0 w-full h-[70px] z-50 transition-all duration-300 ${scrolled ? 'bg-black/60 supports-[backdrop-filter]:backdrop-blur-xl backdrop-blur-xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.25)]' : 'bg-transparent'}`}>

      {/* 3-column grid: logo | nav | avatar — stable at any zoom level */}
      <div className="w-full px-6 lg:px-10 h-full grid grid-cols-3 items-center">

        {/* Col 1 — Logo left */}
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img src={ucdpLogo} alt="UCDP" className="h-18 md:h-24 w-auto rounded-lg" />
          </Link>
        </div>

        {/* Col 2 — Nav links centered */}
        <div className="hidden lg:flex items-center justify-center space-x-6">
          <div className="relative">
            <Link to="/" className={`${navLinkBaseStyle} !text-white hover:!text-white`}>Home</Link>
            {isActiveRoute('/') && <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2" style={{ width: 45, height: 2, backgroundColor: '#FF541F', borderRadius: 1.5 }} />}
          </div>

          {auth.currentUser && (perms.startups || perms.vcIntel || perms.trends) && (
            <div className="relative" ref={exploreRef}>
              <button onClick={() => { setShowExploreDropdown(!showExploreDropdown); setShowTrackDropdown(false); }}
                className={`${navLinkBaseStyle} !text-white hover:!text-white flex items-center gap-1`}>
                Explore <FiChevronDown className={`w-3.5 h-3.5 transition-transform ${showExploreDropdown ? 'rotate-180' : ''}`} />
              </button>
              {isExploreActive() && !showExploreDropdown && <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2" style={{ width: 45, height: 2, backgroundColor: '#FF541F', borderRadius: 1.5 }} />}
              {showExploreDropdown && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-64 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 pb-2">
                  <SectionLabel label="Research" />
                  <DropdownLink to="/discover" icon={<FiGlobe className="w-4 h-4 text-orange-400" />} label="Use Case Discovery" sub="Search Startup use cases" onClick={closeAll} />
                  {perms.startups && <DropdownLink to="/startups" icon={<FiLayers className="w-4 h-4 text-purple-400" />} label="Startup Search" sub="Browse & filter startups" onClick={closeAll} />}
                  {perms.trends && <DropdownLink to="/usecase-trends" icon={<FiTrendingUp className="w-4 h-4 text-yellow-400" />} label="Trends" sub="Emerging technology trends" onClick={closeAll} />}
                  <DropdownLink to="/use-cases" icon={<FiZap className="w-4 h-4 text-sky-400" />} label="Saved Use Cases" sub="Your bookmarked use cases" onClick={closeAll} />
                  {perms.vcIntel && (<><div className="mx-4 my-1 border-t border-white/10" /><SectionLabel label="Invest & Partner" /><DropdownLink to="/vc-intelligence" icon={<FiBarChart2 className="w-4 h-4 text-green-400" />} label="VC Intelligence" sub="Funding trends & investor match" onClick={closeAll} /></>)}
                </div>
              )}
            </div>
          )}

          {auth.currentUser && (perms.engagementRepo || perms.acquisitions) && (
            <div className="relative" ref={trackRef}>
              <button onClick={() => { setShowTrackDropdown(!showTrackDropdown); setShowExploreDropdown(false); }}
                className={`${navLinkBaseStyle} !text-white hover:!text-white flex items-center gap-1`}>
                Track <FiChevronDown className={`w-3.5 h-3.5 transition-transform ${showTrackDropdown ? 'rotate-180' : ''}`} />
              </button>
              {isTrackActive() && !showTrackDropdown && <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2" style={{ width: 45, height: 2, backgroundColor: '#FF541F', borderRadius: 1.5 }} />}
              {showTrackDropdown && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-56 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  {perms.engagementRepo && <DropdownLink to="/engagement-repo" icon={<FiBookmark className="w-4 h-4 text-pink-400" />} label="Engagement Repo" sub="Track startup interactions" onClick={closeAll} />}
                  {perms.acquisitions && <DropdownLink to="/acquisition-dashboard" icon={<FiDollarSign className="w-4 h-4 text-amber-400" />} label="M&A Intelligence" sub="Discover, watchlist & deal signals" onClick={closeAll} />}
                </div>
              )}
            </div>
          )}

          {auth.currentUser && perms.controlCenter && (
            <div className="relative">
              <button className={`${navLinkBaseStyle} !text-white hover:!text-white flex items-center gap-1`} onClick={() => setShowControlDropdown(prev => !prev)}>
                Control Center <FiChevronDown className={`transition-transform ${showControlDropdown ? "rotate-180" : ""}`} />
              </button>
              {isActiveRoute('/admin') && <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2" style={{ width: 45, height: 2, backgroundColor: '#FF541F', borderRadius: 1.5 }} />}
              {showControlDropdown && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl z-50 py-2">
                  <Link to="/admin" onClick={() => setShowControlDropdown(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-colors">
                    <FiSettings className="w-4 h-4 text-orange-400" /> Startup Management
                  </Link>
                  {perms.manageUsers && (
                    <Link to="/manage-users" onClick={() => setShowControlDropdown(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-colors">
                      <FiLayers className="w-4 h-4 text-blue-400" /> User Management
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Col 3 — Avatar/Login right */}
        <div className="flex items-center justify-end space-x-4">

          <div className="hidden lg:block">
            {!auth.currentUser ? (
              <Link to="/login" className="px-4 py-2 rounded-lg !text-white transition-colors"
                style={{ background: '#FF541F', fontFamily: 'Lora', fontSize: '16px', fontWeight: 400 }}>
                Login
              </Link>
            ) : (
              <div className="relative">
                <button onClick={() => setShowDropdown(prev => !prev)}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm" style={{ background: '#FF541F' }}>
                  {auth.currentUser.email?.[0]?.toUpperCase() || "U"}
                </button>
                {showDropdown && (
                  <div ref={dropdownRef} className="absolute right-0 top-full mt-2 w-56 bg-white text-black rounded-xl shadow-2xl z-[1000]">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="font-semibold text-sm text-gray-700">Signed in as</div>
                      <div className="text-orange-700 font-bold truncate">{auth.currentUser.email}</div>
                    </div>
                    <button className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-orange-50 text-red-600 font-semibold rounded-b-xl transition" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <FiX className="w-6 h-6 text-white" /> : <FiMenu className="w-6 h-6 text-white" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[72px] bg-black/40 supports-[backdrop-filter]:backdrop-blur-xl backdrop-blur-xl z-50 overflow-y-auto">
          <div className="px-4 py-4 space-y-3 max-w-md mx-auto">


            {/* Explore */}
            {auth.currentUser && (perms.startups || perms.vcIntel || perms.trends) && (
              <div>
                <button onClick={() => setMobileExploreOpen(!mobileExploreOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-[#FF541F]/10 hover:bg-[#FF541F]/20 transition-colors text-white">
                  <div className="flex items-center space-x-3">
                    <span className="p-2 rounded-lg bg-[#FF541F]/20"><FiSearchIcon className="text-[#FF541F] w-5 h-5" /></span>
                    <span className="font-medium">Explore</span>
                  </div>
                  <FiChevronDown className={`w-4 h-4 transition-transform ${mobileExploreOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileExploreOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link to="/discover" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                      <FiGlobe className="w-4 h-4 text-orange-400" /><span className="text-sm">Use Case Discovery</span>
                    </Link>
                    {perms.startups && (
                      <Link to="/startups" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                        <FiLayers className="w-4 h-4 text-purple-400" /><span className="text-sm">Startup Search</span>
                      </Link>
                    )}
                    {perms.trends && (
                      <Link to="/usecase-trends" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                        <FiTrendingUp className="w-4 h-4 text-yellow-400" /><span className="text-sm">Trends</span>
                      </Link>
                    )}
                    <Link to="/use-cases" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                      <FiZap className="w-4 h-4 text-sky-400" /><span className="text-sm">Saved Use Cases</span>
                    </Link>
                    {perms.vcIntel && (
                      <Link to="/vc-intelligence" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                        <FiBarChart2 className="w-4 h-4 text-green-400" /><span className="text-sm">VC Intelligence</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Track */}
            {auth.currentUser && (perms.engagementRepo || perms.acquisitions) && (
              <div>
                <button onClick={() => setMobileTrackOpen(!mobileTrackOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-pink-500/10 hover:bg-pink-500/20 transition-colors text-white">
                  <div className="flex items-center space-x-3">
                    <span className="p-2 rounded-lg bg-pink-500/20"><FiBookmark className="text-pink-300 w-5 h-5" /></span>
                    <span className="font-medium">Track</span>
                  </div>
                  <FiChevronDown className={`w-4 h-4 transition-transform ${mobileTrackOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileTrackOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    {perms.engagementRepo && (
                      <Link to="/engagement-repo" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                        <FiBookmark className="w-4 h-4 text-pink-400" /><span className="text-sm">Engagement Repo</span>
                      </Link>
                    )}
                    {perms.acquisitions && (
                      <Link to="/acquisition-dashboard" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-white">
                        <FiDollarSign className="w-4 h-4 text-amber-400" /><span className="text-sm">M&A Intelligence</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Control Center */}
            {auth.currentUser && perms.controlCenter && (
              <Link to="/admin" className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors text-white" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="p-2 rounded-lg bg-red-500/20"><FiSettings className="text-red-300 w-5 h-5" /></span>
                <div><span className="font-medium">Control Center</span><p className="text-sm text-white/70">Manage startups & data</p></div>
              </Link>
            )}

            {/* About */}
            <Link to="/about-us" className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="p-2 rounded-lg bg-white/10"><FiAward className="text-orange-300 w-5 h-5" /></span>
              <div><span className="font-medium">About</span><p className="text-sm text-white/70">Learn about us</p></div>
            </Link>

            {/* Auth */}
            <div>
              {!auth.currentUser ? (
                <Link to="/login" className="flex items-center justify-center w-full px-4 py-3 text-white rounded-lg font-medium"
                  style={{ background: '#FF541F' }} onClick={() => setIsMobileMenuOpen(false)}>
                  Login to your account
                </Link>
              ) : (
                <button onClick={handleLogout} className="flex items-center justify-center w-full px-4 py-3 bg-white/5 text-red-300 rounded-lg hover:bg-white/10 transition-colors font-medium">
                  Logout from account
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
